/**
 * Payment Status Page
 * Displays payment verification status after Cashfree checkout
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertCircle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';

const PaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order_id');
  
  // Check if Cashfree passed payment status in URL
  const cashfreeStatus = searchParams.get('status'); // Cashfree may pass status parameter

  const [status, setStatus] = useState('verifying'); // verifying, success, failed, cancelled
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (orderId) {
      verifyPaymentStatus();
    } else {
      setStatus('failed');
      setError('Order ID not found');
    }
  }, [orderId, retryCount]);

  /**
   * Verify payment status from database
   */
  const verifyPaymentStatus = async () => {
    try {
      setStatus('verifying');
      setError(null);

      // Check if status was passed from Cashfree (failed/cancelled)
      if (cashfreeStatus === 'failed' || cashfreeStatus === 'cancelled') {
        console.log(`⚠️ Payment ${cashfreeStatus} - fetching order details...`);
        
        // Fetch order details
        const { data: order, error: orderError } = await supabase
          .from('orders')
          .select(`
            *,
            order_items (
              *,
              menu_items (name, image_url)
            )
          `)
          .eq('id', orderId)
          .single();

        if (!orderError && order) {
          setOrderDetails(order);
          
          // Update order status to failed/cancelled
          // ⚠️ IMPORTANT: Use UnifiedOrderService to trigger coin refund
          const { default: UnifiedOrderService } = await import('../services/unifiedOrderService');
          
          // Update payment status first
          await supabase
            .from('orders')
            .update({
              payment_status: 'failed',
              updated_at: new Date().toISOString()
            })
            .eq('id', orderId);
          
          // Then update order status through service (triggers coin refund)
          await UnifiedOrderService.updateOrderStatus(orderId, 'cancelled', null, 'system');
        }
        
        setStatus(cashfreeStatus === 'cancelled' ? 'cancelled' : 'failed');
        setError(cashfreeStatus === 'cancelled' ? 'Payment was cancelled by user' : 'Payment failed');
        return;
      }

      // Wait 3 seconds for webhook to process (first attempt only)
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      console.log('🔍 Verifying payment status for order:', orderId);

      // Fetch order details from database
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            *,
            menu_items (name, image_url)
          )
        `)
        .eq('id', orderId)
        .single();

      if (orderError) {
        console.error('❌ Error fetching order:', orderError);
        throw new Error('Failed to fetch order details');
      }

      console.log('📦 Order details:', order);
      setOrderDetails(order);

      // Map payment status
      // Payment completed = success (order can be in any active status: confirmed, assigned, started, ready, completed, delivered)
      if (order.payment_status === 'completed' && order.status !== 'cancelled') {
        setStatus('success');
      } else if (order.payment_status === 'failed' || order.status === 'cancelled') {
        setStatus('failed');
      } else if (order.payment_status === 'pending') {
        // Still pending - retry after delay
        if (retryCount < 5) {
          console.log('⏳ Payment still pending, retrying...', retryCount + 1);
          setTimeout(() => setRetryCount(retryCount + 1), 2000);
        } else {
          // Timeout - likely payment failed, mark as cancelled
          console.log('⏰ Payment verification timeout - marking as failed');
          setStatus('failed');
          setError('Payment verification timeout.');
          
          // Auto-cancel the order since webhook didn't update it
          // ⚠️ IMPORTANT: Use UnifiedOrderService to trigger coin refund
          try {
            const { default: UnifiedOrderService } = await import('../services/unifiedOrderService');
            
            // Update payment status first
            await supabase
              .from('orders')
              .update({
                payment_status: 'failed',
                updated_at: new Date().toISOString()
              })
              .eq('id', orderId);
            
            // Then update order status through service (triggers coin refund)
            await UnifiedOrderService.updateOrderStatus(orderId, 'cancelled', null, 'system');
            
            console.log('✅ Order automatically cancelled due to timeout');
          } catch (cancelError) {
            console.error('❌ Error auto-cancelling order:', cancelError);
          }
        }
      }


    } catch (err) {
      console.error('❌ Payment verification error:', err);
      setError(err.message || 'Failed to verify payment status');
      setStatus('failed');
    }
  };

  /**
   * Retry payment verification
   */
  const handleRetry = () => {
    setRetryCount(retryCount + 1);
  };

  /**
   * Navigate to order tracking - Opens live order tracking in menu page
   */
  const handleViewOrder = () => {
    const restaurantId = orderDetails?.restaurant_id;
    const tableId = orderDetails?.table_id;
    
    // Navigate back to menu with order tracking enabled
    if (restaurantId && tableId) {
      navigate(`/menu/${restaurantId}/${tableId}?track_order=${orderId}`);
    } else if (restaurantId) {
      navigate(`/menu/${restaurantId}?track_order=${orderId}`);
    } else {
      // Fallback to generic track order page
      navigate(`/track-order?order_id=${orderId}`);
    }
  };

  /**
   * Navigate back to menu (restaurant-specific)
   */
  const handleBackToMenu = () => {
    const restaurantId = orderDetails?.restaurant_id;
    const tableId = orderDetails?.table_id;
    if (restaurantId && tableId) {
      navigate(`/menu/${restaurantId}/${tableId}`);
    } else if (restaurantId) {
      navigate(`/menu/${restaurantId}`);
    } else {
      navigate('/menu');
    }
  };

  /**
   * Retry payment - create new order
   */
  const handleRetryPayment = () => {
    const restaurantId = orderDetails?.restaurant_id;
    const tableId = orderDetails?.table_id;
    if (restaurantId && tableId) {
      navigate(`/menu/${restaurantId}/${tableId}`);
    } else {
      navigate('/menu');
    }
  };

  /**
   * Render status content based on current status
   */
  const renderStatusContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="status-verifying text-center py-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-blue-100 rounded-full mb-6">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
            <p className="text-gray-600 mb-4">Please wait while we confirm your payment</p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="status-success text-center py-12 px-4 max-w-md mx-auto">
            {/* Simple Animated Success Icon */}
            <div className="relative inline-block mb-8">
              {/* Soft glow background */}
              <div className="absolute inset-0 bg-green-100 rounded-full blur-2xl opacity-50"></div>
              {/* Main circle */}
              <div className="relative w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-14 h-14 text-green-600" strokeWidth={2} />
              </div>
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Successful! 🎉
            </h2>
            <p className="text-sm text-gray-600 mb-8">
              Thank you for your order
            </p>

            {/* Order Details - Minimal Card */}
            {orderDetails && (
              <div className="bg-gray-50 rounded-2xl p-5 mb-6 text-left">
                <div className="space-y-3">
                  {/* Order Number */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Order Number</span>
                    <span className="font-mono text-xs font-semibold text-gray-900">
                      {orderDetails.order_number}
                    </span>
                  </div>
                  
                  {/* Amount Paid */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Amount Paid</span>
                    <span className="text-xl font-bold text-green-600">
                      ₹{orderDetails.total_amount?.toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Items */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Items</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {orderDetails.order_items?.length || 0} items
                    </span>
                  </div>
                  
                  {/* Payment Method */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Payment Method</span>
                    <span className="text-sm font-semibold text-gray-900 capitalize">
                      {orderDetails.payment_method}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Info Message */}
            <div className="bg-blue-50 rounded-xl p-4 mb-8">
              <p className="text-blue-800 text-xs leading-relaxed">
                ✨ Your order has been confirmed and will be prepared shortly. You can track your order status in real-time.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleViewOrder}
                className="w-full px-6 py-3.5 bg-[#00C853] text-white rounded-xl font-bold hover:bg-[#00B248] transition-colors"
              >
                Track Order
              </button>
              <button
                onClick={handleBackToMenu}
                className="w-full px-6 py-3.5 bg-white border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <Home className="w-4 h-4" />
                Back to Menu
              </button>
            </div>
          </div>
        );

      case 'failed':
        return (
          <div className="status-failed text-center py-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 rounded-full mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-4">Your order was not placed</p>
            <p className="text-sm text-gray-500 mb-8">No amount has been deducted from your account</p>

            {/* Error Details */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 max-w-md mx-auto">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Order Details if available */}
            {orderDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-8 text-left max-w-md mx-auto">
                <div className="text-sm text-gray-600 space-y-2">
                  <div className="flex justify-between">
                    <span>Order Number:</span>
                    <span className="font-mono font-medium">{orderDetails.order_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Amount:</span>
                    <span className="font-medium">₹{orderDetails.total_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className="font-medium text-red-600">Cancelled</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <button
                onClick={handleRetryPayment}
                className="flex-1 px-6 py-3 bg-[#00C853] text-white rounded-lg font-semibold hover:bg-[#00B248] transition-colors shadow-[0_4px_0_0_rgba(0,0,0,0.2)]"
              >
                Try Again
              </button>
              <button
                onClick={handleBackToMenu}
                className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back to Menu
              </button>
            </div>

            {/* Support Link */}
            <div className="mt-8">
              <a href="/support" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Need help? Contact Support →
              </a>
            </div>
          </div>
        );

      case 'cancelled':
        return (
          <div className="status-cancelled text-center py-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-yellow-100 rounded-full mb-6">
              <AlertCircle className="w-12 h-12 text-yellow-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Payment Cancelled</h2>
            <p className="text-gray-600 mb-8">You cancelled the payment</p>

            {/* Info Message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <p className="text-yellow-800 text-sm">
                Your order is still pending. You can complete the payment anytime to confirm your order.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <button
                onClick={handleRetryPayment}
                className="flex-1 px-6 py-3 bg-[#00C853] text-white rounded-lg font-semibold hover:bg-[#00B248] transition-colors shadow-[0_4px_0_0_rgba(0,0,0,0.2)]"
              >
                Complete Payment
              </button>
              <button
                onClick={handleBackToMenu}
                className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back to Menu
              </button>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="status-pending text-center py-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-orange-100 rounded-full mb-6">
              <Loader2 className="w-12 h-12 text-orange-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Payment Processing</h2>
            <p className="text-gray-600 mb-8">Your payment is being processed</p>

            {/* Info Message */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <p className="text-orange-800 text-sm">
                This is taking longer than usual. Please wait or check back in a few minutes.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <button
                onClick={handleRetry}
                className="flex-1 px-6 py-3 bg-[#00C853] text-white rounded-lg font-semibold hover:bg-[#00B248] transition-colors flex items-center justify-center gap-2 shadow-[0_4px_0_0_rgba(0,0,0,0.2)]"
              >
                <RefreshCw className="w-5 h-5" />
                Check Status
              </button>
              <button
                onClick={handleBackToMenu}
                className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back to Menu
              </button>
            </div>
          </div>
        );

      default: // error
        return (
          <div className="status-error text-center py-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <AlertCircle className="w-12 h-12 text-gray-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Something Went Wrong</h2>
            <p className="text-gray-600 mb-8">{error || 'Unable to verify payment status'}</p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <button
                onClick={handleRetry}
                className="flex-1 px-6 py-3 bg-[#00C853] text-white rounded-lg font-semibold hover:bg-[#00B248] transition-colors flex items-center justify-center gap-2 shadow-[0_4px_0_0_rgba(0,0,0,0.2)]"
              >
                <RefreshCw className="w-5 h-5" />
                Retry Verification
              </button>
              <button
                onClick={handleBackToMenu}
                className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back to Menu
              </button>
            </div>

            {/* Support Link */}
            <div className="mt-8">
              <a href="/support" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Contact Support for Help →
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="payment-status-page min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg p-8">
        {renderStatusContent()}
      </div>
    </div>
  );
};

export default PaymentStatus;
